import debounce from 'lodash.debounce';
import { wait } from '@/utils/helpers';
import { logger } from '@/utils/logger';
import { ComponentRegistry } from '../fss-input-common/fss-input.interfaces';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { suggestions } from '@/services/suggestions/simple/suggestions';
import { SUGGESTION_LIVE, SUGGESTION_LOCAL } from '@/constants/index';
import { Suggestion } from '@/services/suggestions/simple/suggestionsInterface';
import { trackingService } from '../../../../search-library/src/services/tracking/trackingService';
import { getReferencePath, sanitizerChars } from '@/utils/utils-url';
import { getSearchResultsPath } from '@/utils/search-results-page';
import { QUERY_PARAM_REFERER } from '../../../../search-library/src/constants/params';
import { initializeComponentRegistry } from '@owc/component-registry-connection';

export interface IComponentConfig {
  payload: { searchPagePath: string };
}

@Component({
  name: 'fss-search-input',
})
export default class FssInput extends Vue {
  query = '';
  typedTerm = '';
  arrowIndex = -1;
  isFocused = false;
  blurTimeout!: number;
  isSuggestion = false;
  searchPageFullUrl = '';
  suggestions: Suggestion[] = [];
  SUGGESTION_LIVE = SUGGESTION_LIVE;
  SUGGESTION_LOCAL = SUGGESTION_LOCAL;
  componentRegistry!: ComponentRegistry;
  debouncedInput = debounce(this.onInput, 250);

  @Prop()
  componentId!: string;

  @Prop()
  appId!: string;

  @Prop()
  sp!: string;

  get inputElement(): HTMLInputElement {
    return this.$refs.searchInput as HTMLInputElement;
  }

  get showLabelHelp(): boolean {
    return this.query.length < 3 && this.isFocused;
  }

  mounted(): void {
    this.componentRegistry = initializeComponentRegistry() as ComponentRegistry;
    this.componentRegistry.subscribe(this.componentId, this.onComponentData);

    if (this.appId) {
      this.searchPageFullUrl = getSearchResultsPath();
    }
  }

  onComponentData(componentConfig: IComponentConfig): void {
    if (componentConfig.payload && componentConfig.payload.searchPagePath) {
      const { protocol, host } = window.location;
      this.searchPageFullUrl = `${protocol}//${host}/${componentConfig.payload.searchPagePath}`;
    }
  }

  beforeDestroy(): void {
    this.componentRegistry.unsubscribe(this.componentId, this.onComponentData);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
    }
  }

  onKeyUp(): void {
    this.arrowIndex--;

    if (this.arrowIndex < -1) {
      this.arrowIndex = this.suggestions.length - 1;
    }
  }

  onKeyDown(): void {
    this.arrowIndex++;

    if (this.arrowIndex > this.suggestions.length - 1) {
      this.arrowIndex = -1;
    }
  }

  onEscape(): void {
    this.inputElement.blur();
  }

  onInput(): void {
    if (this.query.length > 2) {
      suggestions.fetchSuggestions(this.query).then((data: any) => {
        this.suggestions = data;
        this.arrowIndex = -1;
      });
    } else {
      this.showSearchHistory();
    }
  }

  onClearInput(): void {
    window.clearTimeout(this.blurTimeout);
    this.query = '';
    this.inputElement.focus();
  }

  setIsFocused(value: boolean): void {
    this.isFocused = value;
  }

  async openInputField(): Promise<void> {
    this.setIsFocused(true);

    await wait(50);
    this.inputElement.focus();
  }

  async onInputFocus(): Promise<void> {
    logger.log('Input has gained focus.');
    this.setIsFocused(true);
    this.dispatchIsFocusedCustomEvent(true);

    if (!this.query.length) {
      // wait for "growing input" animation to finish
      await wait(150);
      this.showSearchHistory();
    }
  }

  onInputBlur(): void {
    logger.log('Input has lost focus. e');
    this.blurTimeout = setTimeout(() => {
      this.resetSuggestions();
      this.setIsFocused(false);
      this.dispatchIsFocusedCustomEvent(false);
    }, 200);
  }

  /**
   * Used to inform the Header when the input has gained or lost focus
   */
  dispatchIsFocusedCustomEvent(isFocused: boolean): void {
    const params = { detail: { isFocused }, bubbles: true, composed: true };
    this.$el.dispatchEvent(new CustomEvent('fssOnInputFocusChange', params));
    logger.log('fssOnInputFocusChange custom event has been dispatched. Payload details: ', { isFocused });
  }

  showSearchHistory(): void {
    this.suggestions = suggestions.getAllLocalSuggestions();
  }

  resetSuggestions(): void {
    this.suggestions = [];
    logger.log('Suggestions were reset.');
  }

  onSuggestionClick(index: number): void {
    window.clearTimeout(this.blurTimeout);
    logger.log('Suggestion with the id', index, ' selected');
    this.arrowIndex = index;

    this.doSearch();
  }

  doSearch(): void {
    this.query = sanitizerChars(this.query);

    if (this.arrowIndex > -1 && this.arrowIndex < this.suggestions.length) {
      const selectedItem: Suggestion = this.suggestions[this.arrowIndex];
      this.typedTerm = this.query;
      this.query = sanitizerChars(selectedItem.raw);
      this.isSuggestion = selectedItem.isSuggest;
    } else {
      this.isSuggestion = false;
    }

    if (!this.query) {
      logger.log('Query is empty ', this.query);
      return undefined;
    }

    suggestions.addSuggestion({ isSuggest: this.isSuggestion, raw: this.query, default: false });

    this.resetSuggestions();
    logger.log('search ', this.query, this.isSuggestion);

    this.redirectToSearchResultsPage();
  }

  redirectToSearchResultsPage(): void {
    trackingService.searchExecuted({ term: this.query, typedQuery: this.typedTerm, numResults: '' });
    window.location.href = this.generateLocationHref();
  }

  generateLocationHref(): string {
    let sp = '';
    if (this.appId) {
      sp = `&sp=${this.sp}`;
    }
    return `${this.searchPageFullUrl}?q=${this.query}${sp}&isSuggestion=${
      this.isSuggestion
    }&${QUERY_PARAM_REFERER}=${getReferencePath()}`;
  }

  onRemoveSuggestion(suggestion: Suggestion, index: number): void {
    window.clearTimeout(this.blurTimeout);
    const searchInput = this.$refs.searchInput as HTMLInputElement;
    searchInput.focus();

    suggestions.removeSuggestion(suggestion);

    this.suggestions.splice(index, 1);

    logger.debug('removed suggestion', suggestion);

    if (this.query.length < 3) {
      return;
    }

    suggestions.fetchSuggestions(this.query).then((data: any) => {
      this.suggestions = data;
    });
  }
}
